import { useEffect, useState } from 'react';
import { studyVariantItems, type StudyVariantItem, type StudyVariantItemInput } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { formatMoney } from '@/lib/money';

/** Typy opracowań z dozwolonymi wariantami (zgodne z backendowym StudyType.variants()). */
const STUDY_TYPES: { code: string; name: string; abbr: string; variants: string[] }[] = [
  { code: 'PRG_1A', name: 'Projekt robót geol. — wykonanie otworu', abbr: 'PRG', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX'] },
  { code: 'PRG_1B', name: 'Projekt robót geol. — likwidacja', abbr: 'PRG', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX'] },
  { code: 'PRG_1C', name: 'Projekt robót geol. — rekonstrukcja', abbr: 'PRG', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX'] },
  { code: 'KIP_2', name: 'Karta informacyjna przedsięwzięcia', abbr: 'KIP', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX'] },
  { code: 'OP_WOD_3A', name: 'Operat w-p — urządzenie wodne', abbr: 'Op-wod', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_PUMPING', 'VC_MONITORING'] },
  { code: 'OP_WOD_3B', name: 'Operat w-p — pobór wód', abbr: 'Op-wod', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_PUMPING', 'VC_MONITORING'] },
  { code: 'OP_WOD_3C', name: 'Operat w-p — odprowadzenie', abbr: 'Op-wod', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_PUMPING', 'VC_MONITORING'] },
  { code: 'DOK_HYDRO_4A', name: 'Dok. hydrogeol. — zasoby eksploatacyjne', abbr: 'Dok-hydro', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_ZONES'] },
  { code: 'DOK_HYDRO_4B', name: 'Dok. hydrogeol. — wydajność otworów', abbr: 'Dok-hydro', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_ZONES'] },
  { code: 'DOK_HYDRO_4C', name: 'Dok. hydrogeol. — strefy ochronne', abbr: 'Dok-hydro', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_ZONES'] },
  { code: 'DOK_LIKW_5', name: 'Dok. geol. z likwidacji otworu', abbr: 'Dok-likw', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX'] },
  { code: 'ANALIZA_RYZYKA_6', name: 'Analiza ryzyka', abbr: 'An-ryz', variants: ['SIMPLE', 'COMPLEX'] },
  { code: 'PLAN_BEZP_7', name: 'Plan bezpieczeństwa wody (WSP)', abbr: 'Plan-bezp', variants: ['SIMPLE', 'COMPLEX'] },
  { code: 'PLAN_RUCHU_8', name: 'Plan Ruchu', abbr: 'Plan-ruchu', variants: ['SIMPLE', 'COMPLEX'] },
];

export const VARIANT_LABEL: Record<string, string> = {
  SIMPLE: 'prosty', COMPLEX: 'złożony', VERY_COMPLEX: 'bardzo złożony',
  VC_PUMPING: 'b. złożony — pompowanie', VC_MONITORING: 'b. złożony — monitoring', VC_ZONES: 'b. złożony — strefy',
};

/** Słownik podpowiadanych pozycji kosztorysu per (typ opracowania × wariant). Edytowany przez OWNER/ADMIN;
 *  kreator oferty podpowiada te pozycje po wyborze typu+wariantu. */
export function StudyVariantItemsAdmin() {
  const [studyType, setStudyType] = useState('OP_WOD_3B');
  const [variant, setVariant] = useState('COMPLEX');
  const [rows, setRows] = useState<StudyVariantItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const variants = STUDY_TYPES.find(t => t.code === studyType)?.variants ?? ['SIMPLE', 'COMPLEX'];

  const reload = () => {
    setRows(null); setErr(null);
    studyVariantItems.list(studyType, variant).then(setRows).catch(e => setErr(e?.message ?? String(e)));
  };
  useEffect(reload, [studyType, variant]);
  // gdy nowy typ nie ma bieżącego wariantu — przełącz na pierwszy dozwolony
  useEffect(() => { if (!variants.includes(variant)) setVariant(variants[0]); /* eslint-disable-next-line */ }, [studyType]);

  const addRow = async () => {
    setBusy(true); setErr(null);
    try {
      const nextOrder = rows && rows.length ? Math.max(...rows.map(r => r.sortOrder)) + 10 : 10;
      await studyVariantItems.create({ studyType, variant, description: 'Nowa pozycja', unit: 'kpl.', defaultQuantity: 1, unitPriceNet: 100, sortOrder: nextOrder });
      reload();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  const saveRow = async (r: StudyVariantItem, patch: Partial<StudyVariantItemInput>) => {
    setErr(null);
    try { await studyVariantItems.update(r.id, patch); reload(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const delRow = async (r: StudyVariantItem) => {
    if (!confirm(`Usunąć pozycję „${r.description}"?`)) return;
    setErr(null);
    try { await studyVariantItems.delete(r.id); reload(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const total = (rows ?? []).reduce((s, r) => s + Number(r.defaultQuantity) * Number(r.unitPriceNet), 0);

  return (
    <div className="max-w-[1200px]">
      <header className="mb-5">
        <h1>Pozycje kosztorysu (per wariant)</h1>
        <div className="text-ink-400 text-[13px]">
          Słownik podpowiadanych pozycji dla kreatora oferty. Wybierz typ opracowania i wariant, aby edytować zestaw pozycji.
        </div>
      </header>

      <div className="card mb-4">
        <div className="card-body grid grid-cols-2 gap-3">
          <label className="field">
            <span className="field-label">Typ opracowania</span>
            <SearchableSelect value={studyType} onChange={setStudyType}>
              {STUDY_TYPES.map(t => <option key={t.code} value={t.code}>{t.abbr} · {t.name}</option>)}
            </SearchableSelect>
          </label>
          <label className="field">
            <span className="field-label">Wariant</span>
            <SearchableSelect value={variant} onChange={setVariant}>
              {variants.map(v => <option key={v} value={v}>{VARIANT_LABEL[v] ?? v}</option>)}
            </SearchableSelect>
          </label>
        </div>
      </div>

      {err && <div className="text-red-500 mb-3 text-[13px]">Błąd: {err}</div>}

      <div className="card">
        <div className="card-head">
          <div className="card-title">
            Pozycje: {STUDY_TYPES.find(t => t.code === studyType)?.abbr} / {VARIANT_LABEL[variant] ?? variant}
          </div>
        </div>
        <div className="card-body !p-0">
          {!rows && !err && <div className="text-ink-400 p-4">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="text-ink-400 p-6 text-center">Brak pozycji dla tego wariantu. Kliknij „Dodaj pozycję".</div>
          )}
          {rows && rows.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th className="w-16">Kolej.</th>
                  <th>Opis pozycji</th>
                  <th className="w-20">Jedn.</th>
                  <th className="w-24">Ilość</th>
                  <th className="w-28">Cena netto</th>
                  <th className="w-28 text-right">Wartość</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => <ItemRow key={r.id} item={r} onSave={patch => saveRow(r, patch)} onDelete={() => delRow(r)}/>)}
              </tbody>
              <tfoot>
                <tr className="border-t border-ink-200">
                  <td colSpan={5} className="text-right font-semibold text-ink-600">Suma netto</td>
                  <td className="text-right font-mono font-semibold tabular-nums">{formatMoney(total)} zł</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
          <div className="px-4 py-3 border-t border-ink-100">
            <button onClick={addRow} disabled={busy} className="btn btn-primary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj pozycję
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Wiersz z lokalnym stanem edycji; „Zapisz" pojawia się dopiero po zmianie. */
function ItemRow({ item, onSave, onDelete }: {
  item: StudyVariantItem;
  onSave: (patch: Partial<StudyVariantItemInput>) => void;
  onDelete: () => void;
}) {
  const initial = { description: item.description, unit: item.unit, defaultQuantity: item.defaultQuantity, unitPriceNet: item.unitPriceNet, sortOrder: item.sortOrder };
  const [d, setD] = useState(initial);
  useEffect(() => { setD(initial); /* eslint-disable-next-line */ }, [item.id, item.description, item.unit, item.defaultQuantity, item.unitPriceNet, item.sortOrder]);
  const dirty = JSON.stringify(d) !== JSON.stringify(initial);
  const lineNet = (Number(d.defaultQuantity) || 0) * (Number(d.unitPriceNet) || 0);

  return (
    <tr>
      <td><input type="number" value={d.sortOrder} onChange={e => setD({ ...d, sortOrder: Number(e.target.value) })} className="input !py-1 w-14 font-mono text-[12px]"/></td>
      <td><input value={d.description} onChange={e => setD({ ...d, description: e.target.value })} className="input !py-1"/></td>
      <td><input value={d.unit} onChange={e => setD({ ...d, unit: e.target.value })} className="input !py-1 font-mono text-[12px]"/></td>
      <td><input type="number" step="0.001" value={d.defaultQuantity} onChange={e => setD({ ...d, defaultQuantity: Number(e.target.value) })} className="input !py-1 font-mono text-[12px] text-right"/></td>
      <td><input type="number" step="0.01" value={d.unitPriceNet} onChange={e => setD({ ...d, unitPriceNet: Number(e.target.value) })} className="input !py-1 font-mono text-[12px] text-right"/></td>
      <td className="text-right font-mono tabular-nums text-[12px]">{formatMoney(lineNet)} zł</td>
      <td>
        <div className="flex items-center gap-1">
          {dirty && (
            <button onClick={() => onSave(d)} className="text-water-600 hover:text-water-800 p-1" title="Zapisz zmiany">
              <Icon name="check" className="w-3.5 h-3.5"/>
            </button>
          )}
          <button onClick={onDelete} className="text-ink-400 hover:text-red-500 p-1" title="Usuń pozycję">
            <Icon name="x" className="w-3.5 h-3.5"/>
          </button>
        </div>
      </td>
    </tr>
  );
}
